import React, { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import { PhaseAside } from './aside';
import { Error } from './errorPage';
import { Form } from './Form';
import { defaultState, State, VerifyResponse } from './interface';
import { Logo } from './Logo';
import { rpc, RPCError } from './rpc';
import { text } from './text';

const productId = document.body.dataset['exProductId'];
const customerId = document.body.dataset['exCustomerId'];

const App: FC = () => {
    const [state, setState] = useState<State>(defaultState);

    // Initial load - verify marketplace token, then enable form.
    const phase = state.phase;
    useEffect(() => {
        if (phase !== 'loading') return;
        (async () => {
            try {
                document.body.classList.add('c-wait');
                const verify = await rpc<VerifyResponse>('verify', {
                    customerId,
                });
                setState((s) => ({
                    ...s,
                    verifyResults: verify,
                    phase: verify.hostname ? 'done' : 'input',
                }));
            } catch (e) {
                setState((s) => ({
                    ...s,
                    error:
                        e instanceof RPCError
                            ? e.rpcError
                            : {
                                  message: 'Unknown Error',
                                  data: JSON.stringify(e),
                              },
                }));
            }
            document.body.classList.remove('c-wait');
        })();
    }, [phase]);

    // submit data, produce error or thank you.
    const onSubmit = async (): Promise<void> => {
        setState({ ...state, phase: 'submitting' });
        document.body.classList.add('c-wait');
        const host = state.form.host;
        try {
            await rpc('register', { ...state.form, productId, customerId });
            setState((s) => ({ ...s, phase: 'done' }));
        } catch (e) {
            if (e instanceof RPCError) {
                const errorDetails = e.rpcError;
                if (errorDetails.message === 'HostNameInUseError') {
                    setState((s) => ({
                        ...s,
                        phase: 'input',
                        inUse: [...s.inUse, host],
                    }));
                } else if (errorDetails.message === 'HostNameInvalidError') {
                    setState((s) => ({
                        ...s,
                        phase: 'input',
                        hostError: errorDetails.data,
                    }));
                } else {
                    setState((s) => ({
                        ...s,
                        phase: 'done',
                        error: errorDetails,
                    }));
                }
            }
        } finally {
            document.body.scrollTop = 0;
            document.body.classList.remove('c-wait');
        }
    };

    if (state.error) {
        return (
            <Page>
                <Error error={state.error} />
            </Page>
        );
    }

    return (
        <Page>
            <section>
                <PhaseAside phase={state.phase} />
                {state.phase === 'done' ? null : (
                    <Form
                        form={state.form}
                        onChange={(form) => setState({ ...state, form })}
                        onSubmit={onSubmit}
                        disabled={state.phase !== 'input'}
                        hostError={
                            state.inUse.includes(state.form.host)
                                ? text.hostInUseError
                                : state.hostError
                        }
                    />
                )}
            </section>
        </Page>
    );
};

const Page: FC<{
    children: React.ReactNode;
}> = ({ children }) => (
    <>
        <header>
            <Logo editionText={text.editionText} />
        </header>
        {children}
    </>
);

ReactDOM.render(
    customerId && productId ? (
        <App />
    ) : (
        <Page>
            <Error error={text.tokenMissingError} />
        </Page>
    ),
    document.body,
);
